// src/pages/Hr/MyHrSettingsPage.jsx
//
// Employee HR transparency page
// =============================
//
// PURPOSE
// -------
// This page gives every authenticated employee a read-only view of the HR and
// commission configuration that applies to THEMSELVES.
//
// Business reason:
// - employees should understand how commissions are calculated,
// - employees should understand which leave profile, work pattern, fallback
//   working days and balances are used when their leave requests are evaluated,
// - transparency must not become administration: the page must not allow editing
//   and must not expose another user's data.
//
// SECURITY / BOUNDARY MODEL
// -------------------------
// The page never reads a user id from the URL.  It uses the authenticated user
// from AuthContext and the backend endpoint /user-settings/me/transparency.
//
// That endpoint is intentionally self-scoped on the backend as well: the target
// user id is taken from req.user.id.  Even if somebody manually changes a route,
// this page cannot be used to inspect a different employee.
//
// ARCHITECTURE ALIGNMENT
// ----------------------
// - Commission rules are loaded as a read-only self-service view from
//   userService.fetchMyTransparencySettings().
// - Leave rules are rendered by the same VacationSettingsSection component used
//   by the admin User Settings page, but with readOnly=true.
// - The leave section therefore stays aligned with HR/admin configuration while
//   removing every write action in the employee view.
//
// IMPORTANT
// ---------
// Do not add update/save actions here.  HR ownership stays with the superuser
// administration screens.  This page is intentionally transparency-only.

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import { fetchMyTransparencySettings } from "../../services/userService";
import { isRequestCanceled } from "../../services/httpUtils";
import VacationSettingsSection from "../../components/userSettings/VacationSettingsSection";

import "../UserSettingsPage.css";

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00%";
  return `${n.toFixed(2)}%`;
}

function formatMoney(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "∞";
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(2);
}

function getDisplayName(user) {
  const first = String(user?.first_name || "").trim();
  const last = String(user?.last_name || "").trim();
  const full = `${first} ${last}`.trim();
  return full || user?.username || `#${user?.id || ""}`;
}

export default function MyHrSettingsPage() {
  const { t } = useTranslation(["common", "userSettings"]);
  const { authState } = useContext(AuthContext);

  const scrollRef = useRef(null);
  const tabsRef = useRef(null);
  const commissionRef = useRef(null);
  const leaveRef = useRef(null);

  const [activeTab, setActiveTab] = useState("commission");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const currentUserId = authState?.user?.id;

  const currentUser = useMemo(
    () => payload?.user || authState?.user || null,
    [payload, authState?.user]
  );

  const settings = payload?.settings || null;
  const commissionMode = String(settings?.commission_mode || "LINEAR").toUpperCase();
  const tiers = Array.isArray(settings?.commission_tiers) ? settings.commission_tiers : [];
  const tier1RequiresReach = !!settings?.tier1_requires_reach;

  function scrollToSection(sectionRef, tabKey) {
    const scroller = scrollRef.current;
    const el = sectionRef.current;
    if (!scroller || !el) return;

    const tabsH = tabsRef.current?.offsetHeight || 0;
    const top = Math.max(0, el.offsetTop - tabsH - 10);
    scroller.scrollTo({ top, behavior: "smooth" });
    setActiveTab(tabKey);
  }

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;

    let raf = 0;

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const tabsH = tabsRef.current?.offsetHeight || 0;
        const y = scroller.scrollTop + tabsH + 12;
        const leaveTop = leaveRef.current?.offsetTop ?? Number.POSITIVE_INFINITY;
        setActiveTab(y >= leaveTop ? "leave" : "commission");
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchMyTransparencySettings({ signal: ac.signal });
        setPayload(data);
      } catch (e) {
        if (isRequestCanceled(e)) return;
        // eslint-disable-next-line no-console
        console.error("[MyHrSettingsPage] ❌ load failed:", e);
        setError(t("userSettings:selfTransparency.messages.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [t]);

  return (
    <div className="user-settings-container">
      <div className="user-settings-topbar">
        <div>
          <h1 className="user-settings-header">{t("userSettings:selfTransparency.title")}</h1>
          <p className="section-subtitle" style={{ marginTop: 4 }}>
            {t("userSettings:selfTransparency.subtitle")}
          </p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      {currentUser ? (
        <div className="us-user-info">
          <div className="us-user-info-header">
            <h2>{t("userSettings:selfTransparency.employeeTitle")}</h2>
          </div>

          <div className="us-user-details-grid">
            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.id")}</span>
              <span className="us-value">{currentUser.id}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.username")}</span>
              <span className="us-value">{currentUser.username}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:selfTransparency.displayName")}</span>
              <span className="us-value">{getDisplayName(currentUser)}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:selfTransparency.accessMode")}</span>
              <span className="us-value">{t("userSettings:selfTransparency.readOnly")}</span>
            </div>
          </div>
        </div>
      ) : (
        <p>{t("userSettings:states.loadingUserInformation")}</p>
      )}

      <div className="settings-block">
        <div className="settings-scroll" ref={scrollRef}>
          <div className="settings-sticky-tabs" ref={tabsRef}>
            <button
              type="button"
              className={activeTab === "commission" ? "tab-btn active" : "tab-btn"}
              onClick={() => scrollToSection(commissionRef, "commission")}
              disabled={loading}
            >
              {t("userSettings:tabs.commission")}
            </button>

            <button
              type="button"
              className={activeTab === "leave" ? "tab-btn active" : "tab-btn"}
              onClick={() => scrollToSection(leaveRef, "leave")}
              disabled={loading || !currentUserId}
            >
              {t("userSettings:tabs.vacation")}
            </button>
          </div>

          <div ref={commissionRef} className="section-anchor">
            <div className="section-card">
              <div className="section-header">
                <h2>{t("userSettings:selfTransparency.commissionTitle")}</h2>
                <p className="section-subtitle">
                  {t("userSettings:selfTransparency.commissionSubtitle")}
                </p>
              </div>

              {loading && !settings ? (
                <p>{t("userSettings:states.loadingSettings")}</p>
              ) : settings ? (
                <>
                  <div className="settings-row">
                    <label>{t("userSettings:commission.modeLabel")}</label>
                    <span className="settings-value-pill">
                      {t(`userSettings:commission.modes.${commissionMode.toLowerCase()}`, {
                        defaultValue: commissionMode,
                      })}
                    </span>
                  </div>

                  {commissionMode === "LINEAR" && (
                    <div className="settings-grid two-cols" style={{ marginTop: 12 }}>
                      <div className="settings-row settings-row-cardlike">
                        <label>{t("userSettings:commission.fields.commission_service")}</label>
                        <span className="settings-value-pill">
                          {formatPercent(settings.commission_service)}
                        </span>
                      </div>

                      <div className="settings-row settings-row-cardlike">
                        <label>{t("userSettings:commission.fields.commission_product")}</label>
                        <span className="settings-value-pill">
                          {formatPercent(settings.commission_product)}
                        </span>
                      </div>
                    </div>
                  )}

                  {commissionMode === "TIERED" && (
                    <div style={{ marginTop: 14 }}>
                      <p className="tiers-hint">
                        {tier1RequiresReach
                          ? t("userSettings:selfTransparency.tier1RequiresReachOn")
                          : t("userSettings:selfTransparency.tier1RequiresReachOff")}
                      </p>

                      {tiers.length === 0 ? (
                        <p>{t("userSettings:selfTransparency.noTiers")}</p>
                      ) : (
                        <div className="tiers-table-wrapper">
                          <div className="tiers-scroll">
                            <table className="tiers-table">
                              <thead>
                                <tr>
                                  <th>{t("userSettings:tiers.table.from")}</th>
                                  <th>{t("userSettings:tiers.table.to")}</th>
                                  <th>{t("userSettings:tiers.table.servicePercent")}</th>
                                  <th>{t("userSettings:tiers.table.productPercent")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tiers.map((tier) => (
                                  <tr key={tier.id || `${tier.min_amount}-${tier.max_amount}`}>
                                    <td>{formatMoney(tier.min_amount)}</td>
                                    <td>{formatMoney(tier.max_amount)}</td>
                                    <td>{formatPercent(tier.service_rate)}</td>
                                    <td>{formatPercent(tier.product_rate)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      <p className="tiers-hint" style={{ marginTop: 10 }}>
                        {t("userSettings:tiers.rulesHint")}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p>{t("userSettings:selfTransparency.noCommissionSettings")}</p>
              )}
            </div>
          </div>

          <div ref={leaveRef} className="section-anchor">
            <div className="section-card">
              {currentUserId ? (
                <VacationSettingsSection userId={currentUserId} readOnly />
              ) : (
                <p>{t("userSettings:states.loadingUserInformation")}</p>
              )}
            </div>
          </div>

          <div className="settings-scroll-end-spacer" />
        </div>
      </div>
    </div>
  );
}
